import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { Alert, Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTokenMaker } from "../../../hooks";
import {
  TowingEquipmentAdmin,
  useTowingEquipmentAdmin,
} from "../../../hooks/useTowingEquipmentAdmin";
import { ConfirmationDialog } from "../../../widgets/ConfirmationDialog";
import { EquipmentEditDialog } from "./components/EquipmentEditDialog";
import { EquipmentTable } from "./components/EquipmentTable";

export interface EditFormData {
  name: string;
  width: string;
  length: string;
  height: string;
  tow_bar_length: string;
  no_footprint: boolean;
}

export const TowingEquipmentAdminScreen: React.FC = () => {
  const { t } = useTranslation();
  const {
    loading,
    error: hookError,
    towingEquipment,
    createTowingEquipment,
    updateTowingEquipment,
    deleteTowingEquipment,
    refreshTowingEquipment,
  } = useTowingEquipmentAdmin();
  const { url: webURL } = useTokenMaker();

  // Check if we're on production
  const isProduction = window.location.hostname === "app.airplx.com";
  const [selectedEquipment, setSelectedEquipment] =
    useState<TowingEquipmentAdmin | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [editingEquipment, setEditingEquipment] =
    useState<TowingEquipmentAdmin | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [componentError, setComponentError] = useState<string | null>(null);
  const [confirmDeploy, setConfirmDeploy] = useState<boolean>(false);
  const [newEquipmentId, setNewEquipmentId] = useState<string | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Combine errors
  const error = componentError || hookError;

  // Focus container on mount and select first equipment
  useEffect(() => {
    containerRef.current?.focus();

    // If we have a new equipment ID to select after refresh
    if (newEquipmentId && towingEquipment) {
      const index = towingEquipment.findIndex((e) => e.id === newEquipmentId);
      if (index !== -1) {
        setSelectedEquipment(towingEquipment[index]);
        setSelectedIndex(index);
        setNewEquipmentId(null); // Clear after selection
        return;
      }
    }

    // Default behavior: select first equipment if none selected
    if (towingEquipment && towingEquipment.length > 0 && !selectedEquipment) {
      setSelectedEquipment(towingEquipment[0]);
      setSelectedIndex(0);
    }
  }, [towingEquipment, newEquipmentId]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle keyboard events when dialog is open
      if (editingEquipment || isCreating) return;
      if (!towingEquipment || towingEquipment.length === 0) return;

      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        let newIndex = selectedIndex;

        if (newIndex === null) {
          newIndex = 0;
        } else if (e.key === "ArrowDown") {
          newIndex = Math.min(newIndex + 1, towingEquipment.length - 1);
        } else {
          newIndex = Math.max(newIndex - 1, 0);
        }

        setSelectedIndex(newIndex);
        setSelectedEquipment(towingEquipment[newIndex]);

        // Scroll into view
        if (tableRef.current) {
          const row = tableRef.current.querySelector(
            `tbody tr:nth-child(${newIndex + 1})`
          );
          row?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [towingEquipment, selectedIndex, editingEquipment, isCreating]);

  const handleSelectEquipment = (
    equipment: TowingEquipmentAdmin,
    index: number
  ) => {
    setSelectedEquipment(equipment);
    setSelectedIndex(index);
  };

  const handleEditClick = (
    equipment: TowingEquipmentAdmin,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    setEditingEquipment(equipment);
    setIsCreating(false);
  };

  const handleAddClick = () => {
    setEditingEquipment(null);
    setIsCreating(true);
  };

  const handleDeleteClick = async (
    equipment: TowingEquipmentAdmin,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    if (
      window.confirm(t('screens.superAdmin.towingEquipmentAdmin.confirmations.deleteMessage', { name: equipment.name }))
    ) {
      try {
        await deleteTowingEquipment(equipment.id);
        if (selectedEquipment?.id === equipment.id) {
          setSelectedEquipment(null);
          setSelectedIndex(null);
        }
      } catch (err) {
        setComponentError(t('screens.superAdmin.towingEquipmentAdmin.errors.deleteFailed', { error: err }));
      }
    }
  };

  // Helper function for dimension validation
  const validateDimension = (value: string, allowZero: boolean = false) => {
    if (!value) return null;
    const num = parseFloat(value);
    const isValid =
      !isNaN(num) && (allowZero ? num >= 0 : num > 0) && num <= 1000;
    return isValid ? num : null;
  };

  const handleSaveEdit = async (formData: EditFormData) => {
    try {
      if (isCreating) {
        // Create new equipment
        const width = validateDimension(formData.width);
        const length = validateDimension(formData.length);
        const height = validateDimension(formData.height);
        const towBarLength = formData.tow_bar_length
          ? validateDimension(formData.tow_bar_length, true)
          : null;

        if (!formData.name) {
          setComponentError(t('screens.superAdmin.towingEquipmentAdmin.errors.nameRequired'));
          return;
        }

        if (!width || !length || !height) {
          setComponentError(t('screens.superAdmin.towingEquipmentAdmin.errors.invalidDimensions'));
          return;
        }

        const newEquipment = await createTowingEquipment({
          name: formData.name,
          width: width || 0,
          length: length || 0,
          height: height || 0,
          tow_bar_length: towBarLength,
          no_footprint: formData.no_footprint,
        });

        // Check if creation was successful
        if (!newEquipment) {
          setComponentError(t('screens.superAdmin.towingEquipmentAdmin.errors.createFailed'));
          return;
        }

        // Set the new equipment ID for selection after refresh
        if (typeof newEquipment.id === "string") {
          setNewEquipmentId(newEquipment.id);
        }
        await refreshTowingEquipment();
      } else if (editingEquipment) {
        // Update existing equipment
        const width = formData.width ? validateDimension(formData.width) : null;
        const length = formData.length
          ? validateDimension(formData.length)
          : null;
        const height = formData.height
          ? validateDimension(formData.height)
          : null;
        const towBarLength = formData.tow_bar_length
          ? validateDimension(formData.tow_bar_length, true)
          : undefined;

        // Check if any provided dimensions are invalid
        if (formData.width && !width) {
          setComponentError(t('screens.superAdmin.towingEquipmentAdmin.errors.widthInvalid'));
          return;
        }
        if (formData.length && !length) {
          setComponentError(t('screens.superAdmin.towingEquipmentAdmin.errors.lengthInvalid'));
          return;
        }
        if (formData.height && !height) {
          setComponentError(t('screens.superAdmin.towingEquipmentAdmin.errors.heightInvalid'));
          return;
        }

        const updatedEquipment: TowingEquipmentAdmin = {
          ...editingEquipment,
          name: formData.name || editingEquipment.name,
          width: width ?? editingEquipment.width,
          length: length ?? editingEquipment.length,
          height: height ?? editingEquipment.height,
          tow_bar_length:
            towBarLength !== undefined
              ? towBarLength
              : editingEquipment.tow_bar_length,
          no_footprint: formData.no_footprint,
        };

        await updateTowingEquipment(updatedEquipment);

        // Update selected equipment if it was edited
        const updated = towingEquipment.find(
          (e) => e.id === editingEquipment.id
        );
        if (updated && selectedEquipment?.id === editingEquipment.id) {
          setSelectedEquipment(updated);
        }
      }

      setEditingEquipment(null);
      setIsCreating(false);
      setComponentError(null);
    } catch (err) {
      setComponentError(t('screens.superAdmin.towingEquipmentAdmin.errors.saveFailed', { error: err }));
    }
  };

  // Helper to prepare equipment data for export
  const prepareEquipmentExportData = (): {
    headers: string[];
    rows: string[][];
  } => {
    const headers = [
      t('screens.superAdmin.towingEquipmentAdmin.exportHeaders.name'),
      t('screens.superAdmin.towingEquipmentAdmin.exportHeaders.width'),
      t('screens.superAdmin.towingEquipmentAdmin.exportHeaders.length'),
      t('screens.superAdmin.towingEquipmentAdmin.exportHeaders.height'),
      t('screens.superAdmin.towingEquipmentAdmin.exportHeaders.towBarLength'),
      t('screens.superAdmin.towingEquipmentAdmin.exportHeaders.id'),
    ];

    const rows = towingEquipment.map((e) => [
      e.name || "",
      e.width?.toFixed(1) || "",
      e.length?.toFixed(1) || "",
      e.height?.toFixed(1) || "",
      e.tow_bar_length?.toFixed(1) || "",
      e.id,
    ]);

    return { headers, rows };
  };

  // Helper to download file
  const downloadFile = (
    content: string,
    filename: string,
    mimeType: string
  ) => {
    const blob = new Blob([content], { type: mimeType });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const { headers, rows } = prepareEquipmentExportData();

    // Convert to TSV format
    const tsvContent = [
      headers.join("\t"),
      ...rows.map((row) => row.join("\t")),
    ].join("\n");

    const filename = `towing_equipment_${
      new Date().toISOString().split("T")[0]
    }.tsv`;
    downloadFile(
      tsvContent,
      filename,
      "text/tab-separated-values;charset=utf-8;"
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>{t('screens.superAdmin.towingEquipmentAdmin.loading')}</Typography>
      </Box>
    );
  }

  if (error && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" onClose={() => setComponentError(null)}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      tabIndex={0}
      sx={{
        p: 2,
        height: "calc(100vh - 120px)",
        display: "flex",
        flexDirection: "column",
        outline: "none",
      }}
    >
      <Stack spacing={3} sx={{ height: "100%" }}>
        <Stack direction="row" justifyContent="flex-end" alignItems="center">
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
              color="primary"
            >
              {t('screens.superAdmin.towingEquipmentAdmin.buttons.addEquipment')}
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              disabled={towingEquipment.length === 0}
              color="primary"
            >
              {t('screens.superAdmin.towingEquipmentAdmin.buttons.exportList')}
            </Button>
            <Tooltip
              title={
                !isProduction
                  ? t('screens.superAdmin.towingEquipmentAdmin.tooltips.deployOnlyProduction')
                  : ""
              }
              arrow
            >
              <span>
                <Button
                  onClick={() => setConfirmDeploy(true)}
                  variant="contained"
                  color="primary"
                  startIcon={<RocketLaunchIcon />}
                  disabled={!isProduction}
                >
                  {t('screens.superAdmin.towingEquipmentAdmin.buttons.deploy')}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <EquipmentTable
            equipment={towingEquipment}
            selectedEquipment={selectedEquipment}
            onSelectEquipment={handleSelectEquipment}
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteClick}
            tableRef={tableRef}
          />
        </Box>
      </Stack>

      <EquipmentEditDialog
        open={!!editingEquipment || isCreating}
        equipment={editingEquipment}
        isCreating={isCreating}
        onClose={() => {
          setEditingEquipment(null);
          setIsCreating(false);
        }}
        onSave={handleSaveEdit}
      />

      {confirmDeploy && (
        <ConfirmationDialog
          title={t('screens.superAdmin.towingEquipmentAdmin.confirmations.deployTitle')}
          text={t('screens.superAdmin.towingEquipmentAdmin.confirmations.deployMessage')}
          onCancel={() => setConfirmDeploy(false)}
          onClose={() => setConfirmDeploy(false)}
          onOk={async () => {
            await fetch(`${webURL}/jobs/update-towing-equipment`);
            setConfirmDeploy(false);
          }}
          okText={t('screens.superAdmin.towingEquipmentAdmin.confirmations.deployOkButton')}
        />
      )}
    </Box>
  );
};
